/**
 * Is the signed-in user a FLIGHT ADMIN? — the gate on the Rules mode.
 *
 * Aaron's round-2 ruling (P9, 2026-09-27): Flight Admin = the holder of the
 * `flightduty` `view_all_pilots` permission token. This derives it EXACTLY as
 * `canViewAllPilots` in FlightDutyCanduty.tsx (and its siblings), and exactly
 * as the backend's `app/flightduty/access.py::can_view_all_pilots`, which the
 * enabled-rules write route uses — so the screen and the API agree on who may
 * change the setting:
 *
 *   1. `is_admin` / `is_superuser` short-circuit to yes (the SPA's rule; on the
 *      wire both are undefined today — see access.py's docstring).
 *   2. The tenant's `flightduty` / `view_all_pilots` (or `ViewAllPilots`)
 *      config row names a permission token; yes iff the user's `flightduty`
 *      permission bag holds it. An absent bag entry is NO (fail closed).
 *   3. NO such config row at all ⇒ yes, the SPA's `viewAllToken == null → true`
 *      fallback: the tenant never configured the permission. On g2 and mustang
 *      the row exists (787), so this does not fire there.
 *
 * ONE ADDITION, and it is not a change to the rule: `loading`. The Canduty
 * memo reads an unresolved config (`useConfig` → `undefined`) as "no token" and
 * answers YES for the instant before the config arrives. For a data scope that
 * flicker is harmless; for a MENU ENTRY and a ROUTE GATE it would flash the
 * Rules mode at a pilot. So this hook withholds its answer until both the user
 * and the `flightduty` config have resolved, and consumers treat `loading` as
 * "not an admin yet".
 */
import { useMemo } from "react";

import { useConfig } from "../../hooks/useConfig";
import { useCurrentUser } from "../../hooks/useCurrentUser";

export interface FlightAdminState {
  isFlightAdmin: boolean;
  loading: boolean;
}

/** The pure rule, injectable for tests. Mirrors `canViewAllPilots`. */
export function isFlightAdminFor(
  currentUser: { [key: string]: any } | undefined | null,
  dutyConfig: { [key: string]: any } | undefined | null
): boolean {
  if (!currentUser) return false;
  if (currentUser.is_admin || currentUser.is_superuser) return true;
  const permissions = currentUser.permissions as
    | Record<string, number[]>
    | undefined;
  const perms = permissions?.flightduty ?? [];
  const viewAllToken = dutyConfig?.view_all_pilots ?? dutyConfig?.ViewAllPilots;
  if (viewAllToken != null) {
    return perms.includes(Number(viewAllToken));
  }
  return true;
}

export function useIsFlightAdmin(): FlightAdminState {
  const currentUser = useCurrentUser();
  const dutyConfig = useConfig("flightduty");
  return useMemo(() => {
    const loading = currentUser == null || dutyConfig === undefined;
    return {
      loading,
      isFlightAdmin: loading ? false : isFlightAdminFor(currentUser, dutyConfig),
    };
  }, [currentUser, dutyConfig]);
}
